import { useEffect, useState } from 'react';

import { isMotionReduced } from '#src/app/shared/utils/accessibility';

type Options = Readonly<{
	intervalMs: number;
	itemCount: number;
}>;

type Result = ModelHookResult<{
	activeIndex: number;
}>;

/**
 * Cycles `activeIndex` through `0..itemCount - 1` every `intervalMs`.
 *
 * The effect depends only on the two options (both constants at every call site), so parent
 * re-renders never restart the interval. Under `prefers-reduced-motion` the cycle never starts and
 * the index stays frozen at 0 — `isMotionReduced()` also returns true on the server, so it is only
 * ever read inside the effect.
 */
export function useProofCycle({ intervalMs, itemCount }: Options): Result {
	const [activeIndex, setActiveIndex] = useState(0);

	useEffect(() => {
		if (isMotionReduced()) return undefined;

		const id = setInterval(() => {
			setActiveIndex((index) => (index + 1) % itemCount);
		}, intervalMs);

		return () => clearInterval(id);
	}, [intervalMs, itemCount]);

	return {
		data: { activeIndex },
	};
}
